import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { DealRoom } from 'models/DealRoom';
import ListingBanner from './components/ListingBanner';
import EyeIcon from 'icons/Eye.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Form } from 'react-final-form';
import DefaultStep from './components/DefaultStep';
import api from 'api';
import PublicStep from './components/PublicStep';
import { Listing, LISTING_STAGE, ListingStage } from 'models/Listing';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import PauseIcon from 'icons/Pause.icon';
import { ObjectValues } from 'types';
import CircledExclamationmarkIcon from 'icons/CircledExclamationmark.icon';
import {
  checkCanUnpaused,
  checkIsNoPackage,
  checkIsPaused,
  checkIsPublic,
} from 'utils';
import { getFormattedDate, getListingExpirationDate } from 'dateUtils';

import './ManageListingVisibility.styles.scss';
import './ManageListingVisibility.styles.responsive.scss';

const MODAL_STEPS = {
  DEFAULT: 'default',
  PUBLIC: 'public',
} as const;

type StepType = ObjectValues<typeof MODAL_STEPS>;

type ManageListingVisibilityProps = {
  className?: string;
  dealRoom: DealRoom;
  canManageVisibility?: boolean;
  setDealRoom: Dispatch<SetStateAction<DealRoom>>;
  setListing: Dispatch<SetStateAction<Partial<Listing>>>;
};

const ManageListingVisibility: React.FC<ManageListingVisibilityProps> = (
  props,
) => {
  const {
    className,
    dealRoom,
    canManageVisibility = true,
    setDealRoom,
    setListing,
  } = props;

  const { listingId, listingStage, listing } = dealRoom || {};
  const { listingPackage, expirationDate, seekingExpressionOfInterestDate } =
    listing || {};

  const [step, setStep] = useState<StepType>(MODAL_STEPS.DEFAULT);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef();

  const { t } = useTranslation();

  const classes = classNames('manage-listing-visibility', className);

  const isPublic = checkIsPublic(listingStage);
  const isPaused = checkIsPaused(listingStage);
  const isListingNoPackage = checkIsNoPackage(listingPackage);
  const canChangeListingVisibility = !isListingNoPackage;

  const exprDate = useMemo(
    () =>
      getListingExpirationDate(expirationDate, seekingExpressionOfInterestDate),
    [expirationDate, seekingExpressionOfInterestDate],
  );

  const initialValues = useMemo(
    () => ({
      listingStage: isPublic ? LISTING_STAGE.PUBLIC : LISTING_STAGE.PRIVATE,
    }),
    [isPublic],
  );

  const STEPS = useMemo(
    () => ({
      [MODAL_STEPS.DEFAULT]: {
        title: t('setTheVisibilityOfThisListing'),
        Component: DefaultStep,
      },
      [MODAL_STEPS.PUBLIC]: {
        title: t('confirmThatWantPublic'),
        Component: PublicStep,
      },
    }),
    [t],
  );

  const Step = useMemo(() => STEPS[step].Component, [STEPS, step]);

  const unpauseListing = useCallback(async () => {
    try {
      const {
        data: { listing, dealStage },
      } = await api.portfolio.seller.unpauseListing(listingId);

      setListing(listing);
      setDealRoom((prev) => ({
        ...prev,
        listing: { ...prev.listing, ...listing },
        stage: dealStage,
        listingStage: listing.stage,
      }));

      popSuccess(t(SUCCESS_MESSAGES.UNPAUSED_LISTING));
    } catch (err) {
      popServerError(err);
    }
  }, [listingId, popServerError, popSuccess, setDealRoom, setListing, t]);

  const handleSubmit = useCallback(
    async (values: { listingStage: ListingStage }) => {
      const { listingStage } = values;

      if (checkIsPublic(listingStage) && step === MODAL_STEPS.DEFAULT) {
        setStep(MODAL_STEPS.PUBLIC);
        return;
      }

      try {
        await api.dealRoom.changeListingStage(listingId, listingStage);

        setListing((prev) => ({ ...prev, stage: listingStage }));
        setDealRoom((prev) => ({
          ...prev,
          listingStage,
          listing: { ...prev.listing, stage: listingStage },
        }));

        modal.close();
        setStep(MODAL_STEPS.DEFAULT);
      } catch (error) {
        popServerError(error);
      }
    },
    [step, listingId, modal, setListing, setDealRoom, popServerError],
  );

  const ManageModal = useMemo(
    () => (
      <Modal
        ref={modalRef}
        overlayCloses={false}
        title={STEPS[step].title}
        className="manage-listing-visibility-modal"
      >
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={({
            handleSubmit,
            submitting,
            initialValues,
            values: { listingStage },
          }) => (
            <form onSubmit={handleSubmit}>
              <Step />

              <div className="actions">
                {step !== MODAL_STEPS.DEFAULT ? (
                  <Button
                    variant="link"
                    onClick={() => setStep(MODAL_STEPS.DEFAULT)}
                  >
                    {t('goBack')}
                  </Button>
                ) : (
                  <div />
                )}
                <div>
                  <Button
                    variant="link"
                    onClick={() => {
                      modal.close();
                      setStep(MODAL_STEPS.DEFAULT);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      submitting || listingStage === initialValues.listingStage
                    }
                  >
                    {t(
                      step === MODAL_STEPS.DEFAULT &&
                        checkIsPublic(listingStage)
                        ? 'next'
                        : 'confirm',
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
        />
      </Modal>
    ),
    [STEPS, Step, initialValues, modal, step, t, modalRef, handleSubmit],
  );

  const UnpauseButton = useMemo(() => {
    if (!checkCanUnpaused(dealRoom.stage, dealRoom.listingStage)) return null;

    return (
      <Button
        icon={PauseIcon}
        variant="outline"
        disabled={!canManageVisibility}
        onClick={async () => {
          if (!canManageVisibility) return;

          if (
            await confirm(
              {
                title: t('unpauseListing'),
                content: t('areYouSureUnpausingMessage'),
              },
              true,
            )
          )
            unpauseListing();
        }}
      >
        {t('unpause')}
      </Button>
    );
  }, [canManageVisibility, dealRoom, t, unpauseListing]);

  return (
    <div className={classes}>
      {ManageModal}

      {isPaused ? (
        <ListingBanner
          color="neutral"
          title={t('pauseListingTitle')}
          description={t('pauseListingDescription')}
          icon={CircledExclamationmarkIcon}
        >
          {UnpauseButton}
        </ListingBanner>
      ) : (
        <ListingBanner
          color={isPublic ? 'green' : 'orange'}
          icon={isPublic ? EyeIcon : LockclosedIcon}
          title={t(
            isPublic ? 'activeAndPublicTitle' : 'activeAndRestrictedTitle',
          )}
          description={
            isPublic
              ? t('activeAndPublicDescription', {
                  date: exprDate ? getFormattedDate(new Date(exprDate)) : 'NA',
                })
              : t('activeAndRestrictedDescription')
          }
        >
          {canChangeListingVisibility && (
            <Button
              disabled={!canManageVisibility}
              icon={isPublic ? LockclosedIcon : EyeIcon}
              variant="outline"
              onClick={() => {
                if (!canManageVisibility) return;

                modal.open();
              }}
            >
              {t(isPublic ? 'makePrivate' : 'makePublic')}
            </Button>
          )}
        </ListingBanner>
      )}
    </div>
  );
};

export default ManageListingVisibility;
